import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { DripAudience, DripPace, DripPayload, DripPreview, Option, Playbook } from '../api/types'
import { useVocabulary } from '../components/IntakeFields'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { PeoplePicker } from '../components/PeoplePicker'
import { PeopleTable } from '../components/PeopleTable'
import { PlanPreview } from '../components/Playbooks'
import { ErrorState, Loading } from '../components/States'
import { useToast } from '../components/Toast'
import { titleCase } from '../lib/format'
import { useAsync } from '../lib/useAsync'

// Same shape as the workflow builder — numbered cards on the left, a live answer
// on the right — so an officer who has built a workflow already knows this screen.
// The right-hand panel answers the question that matters before launch: how many
// people, and how many of them can actually be emailed or texted.

type ListKey = 'lead_status' | 'campaign' | 'source' | 'purpose' | 'timeline'

const PACES = [10, 25, 50, 100]

// Matches logic.PACE_DEFAULTS on the server. A fresh drip sends inside business
// hours, weekdays only, three minutes apart at the closest.
const DEFAULT_PACE: DripPace = { window: [9, 17], weekdays_only: true, ramp_days: 0, min_gap_mins: 3 }

const HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]
const hourLabel = (h: number) => `${h % 12 || 12}${h < 12 ? 'am' : 'pm'}`
const GAPS = [1, 2, 3, 5, 10, 15, 30]
const RAMPS: [number, string][] = [[0, 'No ramp — full pace from day one'], [3, 'Over 3 days'], [5, 'Over 5 days'], [10, 'Over 10 days']]

export function DripEditor({ isAdmin }: { isAdmin: boolean }) {
  const { dripId } = useParams()
  const editingId = dripId ? Number(dripId) : null
  const nav = useNavigate()
  const toast = useToast()
  const vocab = useVocabulary()
  const { data: meta, error } = useAsync(() => api.drips(), [])
  const { data: wfs } = useAsync(() => api.workflows(), [])
  const { data: existing } = useAsync(() => (editingId ? api.drip(editingId) : Promise.resolve(null)), [editingId])

  const [name, setName] = useState('')
  const [audience, setAudience] = useState<DripAudience>({ owner: 'mine' })
  const [members, setMembers] = useState<{ id: number; name: string | null }[]>([])
  // Arriving from the workflow builder ("send it to a group") preselects it.
  const [params] = useSearchParams()
  const [playbook, setPlaybook] = useState(params.get('playbook') ?? '')
  const [dailyLimit, setDailyLimit] = useState(50)
  const [pace, setPace] = useState<DripPace>(DEFAULT_PACE)
  const [stopOnReply, setStopOnReply] = useState(true)
  const [stopStatuses, setStopStatuses] = useState<string[]>(['application', 'funded', 'dead'])
  const [autoJoin, setAutoJoin] = useState(false)
  const [preview, setPreview] = useState<DripPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [tagInput, setTagInput] = useState('')

  useEffect(() => {
    if (!existing) return
    setName(existing.name)
    setAudience({ owner: 'mine', ...existing.audience })
    setMembers((existing.members ?? []).map((m) => ({ id: m.id, name: m.name })))
    setPlaybook(existing.playbook)
    setDailyLimit(existing.daily_limit)
    setPace({ ...DEFAULT_PACE, ...(existing.pace ?? {}) })
    setStopOnReply(existing.stop_on_reply)
    setStopStatuses(existing.stop_statuses)
    setAutoJoin(existing.auto_join)
  }, [existing])

  // Live count as the audience changes, debounced so typing a tag doesn't fire a
  // query per keystroke.
  const memberIds = useMemo(() => members.map((m) => m.id), [members])
  useEffect(() => {
    const t = window.setTimeout(() => {
      api.previewDrip(audience, memberIds, dailyLimit, pace).then(setPreview).catch(() => setPreview(null))
    }, 300)
    return () => window.clearTimeout(t)
  }, [audience, memberIds, dailyLimit, pace])

  const launched = Boolean(existing?.launched_at)
  const workflows: Playbook[] = wfs?.workflows ?? []
  const workflow = workflows.find((w) => w.key === playbook)
  const leadStatuses: Option[] = vocab?.lead_status ?? []
  const lists: { key: ListKey; label: string; options: Option[] }[] = [
    { key: 'lead_status', label: 'Lead stage', options: leadStatuses },
    { key: 'campaign', label: 'Got a mailer', options: (meta?.campaigns ?? []).map((c) => ({ key: c.campaign, label: `${c.campaign} (${c.leads})` })) },
    { key: 'timeline', label: 'Timeline', options: vocab?.timeline ?? [] },
    { key: 'purpose', label: 'Looking to', options: vocab?.purpose ?? [] },
    { key: 'source', label: 'How they found us', options: vocab?.source ?? [] },
  ]

  // What the drip is aimed at right now, each removable — the filter rows are
  // easy to set and easy to forget.
  const chosen: { key: string; label: string; remove: () => void }[] = []
  for (const l of lists) {
    for (const value of audience[l.key] ?? []) {
      const opt = l.options.find((o) => o.key === value)
      chosen.push({
        key: `${l.key}:${value}`,
        label: `${l.label}: ${opt?.label.replace(/ \(\d+\)$/, '') ?? titleCase(value)}`,
        remove: () => toggle(l.key, value),
      })
    }
  }
  for (const t of audience.tags ?? []) {
    chosen.push({ key: `tag:${t}`, label: `Tagged: ${t}`,
      remove: () => setAudience((a) => ({ ...a, tags: (a.tags ?? []).filter((x) => x !== t) })) })
  }

  const toggle = (key: ListKey, value: string) =>
    setAudience((a) => {
      const current = new Set(a[key] ?? [])
      if (current.has(value)) current.delete(value)
      else current.add(value)
      return { ...a, [key]: [...current] }
    })

  const payload = (): DripPayload => ({
    name: name.trim(), playbook, audience, members: memberIds, auto_join: autoJoin,
    stop_on_reply: stopOnReply, stop_statuses: stopStatuses, daily_limit: dailyLimit, pace,
  })

  const problem = !name.trim() ? 'Give it a name.'
    : !playbook ? 'Pick the workflow it should run.'
      : preview && preview.count === 0 ? 'Nobody matches yet — choose who it goes to.' : ''

  // The buttons stay clickable: a greyed-out Save with no reason is a dead end.
  // Clicking with something missing says what, and takes you to it.
  const nameRef = useRef<HTMLInputElement>(null)
  const [nameMissing, setNameMissing] = useState(false)
  function blocked(needed: string): boolean {
    if (!needed) return false
    toast(needed, { tone: 'warning' })
    if (!name.trim()) {
      setNameMissing(true)
      nameRef.current?.focus()
      nameRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
    return true
  }
  const saveProblem = !name.trim() ? 'Give it a name first — step 1.' : !playbook ? 'Pick the workflow it should run.' : ''

  async function save(thenLaunch: boolean) {
    if (blocked(thenLaunch ? problem : saveProblem)) return
    setBusy(true)
    setErr('')
    try {
      const saved = editingId ? await api.updateDrip(editingId, payload()) : await api.createDrip(payload())
      if (thenLaunch) {
        const out = await api.launchDrip(saved.id)
        toast(`Launched — ${out.started ?? 0} people added. Sending follows the window you set, a few at a time.`, { tone: 'positive' })
      } else {
        toast(launched ? 'Saved' : 'Saved as a draft — nothing has been sent', { tone: 'positive' })
      }
      nav(`/drips/${saved.id}`)
    } catch (e) {
      setErr(errorText(e))
      setConfirming(false)
    } finally {
      setBusy(false)
    }
  }

  if (error) return <div className="page"><ErrorState error={error} /></div>
  if (!meta || !wfs || (editingId && !existing)) return <div className="page"><Loading /></div>

  const days = preview ? Math.max(1, Math.ceil(preview.count / dailyLimit)) : 0
  const taskSteps = workflow?.steps.filter((s) => s.type === 'task').length ?? 0

  return (
    <div className="page workflow-builder campaign-page">
      <div className="crumbs"><Link to="/drips">Drip campaigns</Link> › {editingId ? existing?.name : 'New drip'}</div>
      <div className="page-head">
        <div>
          <h1 className="h1">{editingId ? 'Drip settings' : 'Set up a drip campaign'}</h1>
          <p className="subline">One workflow, sent to a group of people a few at a time. It stops for anyone who replies or applies.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => nav(editingId ? `/drips/${editingId}` : '/drips')}>Cancel</button>
          <button className="btn" onClick={() => save(false)} disabled={busy}>
            {launched ? 'Save' : 'Save draft'}
          </button>
          {!launched ? (
            <button className="btn primary" onClick={() => { if (!blocked(problem)) setConfirming(true) }} disabled={busy}>
              <Icon name="send" size={16} /> Review &amp; launch
            </button>
          ) : null}
        </div>
      </div>

      <div className="wb-grid">
        <div className="stack">
          <section className="card">
            <div className="card-head"><span className="wb-num">1</span><h2 className="h2">Name it</h2></div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              <input ref={nameRef} className="field" value={name} maxLength={80}
                onChange={(e) => { setName(e.target.value); setNameMissing(false) }}
                placeholder="e.g. Refi mailer follow-up — spring" aria-label="Drip name" aria-invalid={nameMissing}
                style={nameMissing ? { borderColor: 'var(--danger)' } : undefined} />
              {nameMissing ? <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>Name it so you can find it later — only you and your team see this.</div> : null}
            </div>
          </section>

          <section className="card">
            <div className="card-head"><span className="wb-num">2</span><h2 className="h2">Who gets it</h2></div>
            <div className="card-body" style={{ paddingTop: 0 }}>
              <div className="aud-bar">
                <span className="quiet" style={{ fontSize: 12.5 }}>
                  Filter the columns to choose who gets it. The list below is exactly who it goes to.
                </span>
                <span className="spacer" />
                {isAdmin ? (
                  <span className="wb-chips">
                    <button className={`chip-btn${audience.owner !== 'all' ? ' on' : ''}`} onClick={() => setAudience((a) => ({ ...a, owner: 'mine' }))}>My contacts</button>
                    <button className={`chip-btn${audience.owner === 'all' ? ' on' : ''}`} onClick={() => setAudience((a) => ({ ...a, owner: 'all' }))}>Everyone’s</button>
                  </span>
                ) : null}
                <button className="linkbtn" onClick={() => { setAudience({ owner: audience.owner ?? 'mine' }); setMembers([]) }}>Clear filters</button>
              </div>

              {preview && !preview.count ? (
                <div className="notice accent" style={{ marginBottom: 8 }}>
                  <Icon name="info" />
                  {!preview.shown_count ? (
                    <span>
                      No contacts here yet.{audience.owner !== 'all' && isAdmin
                        ? <> These are only the ones assigned to you — try <button className="linkbtn" onClick={() => setAudience((a) => ({ ...a, owner: 'all' }))}>everyone’s contacts</button>.</>
                        : ' Import or add contacts first, and they’ll show up here.'}
                    </span>
                  ) : chosen.length ? (
                    <span>Nobody matches every filter. Remove one to widen it.</span>
                  ) : (
                    <span>
                      Nothing picked yet, so this would go to nobody. Filter a column below, or{' '}
                      <button className="linkbtn" onClick={() => setAudience((a) => ({ ...a, everyone: true }))}>
                        send it to all {preview.shown_count.toLocaleString()}
                      </button>.
                    </span>
                  )}
                </div>
              ) : null}
              {audience.everyone ? (
                <div className="aud-bar">
                  <span className="tag">Everyone in view
                    <button onClick={() => setAudience((a) => ({ ...a, everyone: false }))} aria-label="Stop sending to everyone"><Icon name="close" size={13} /></button>
                  </span>
                </div>
              ) : null}

              <PeopleTable
                lists={lists} audience={audience} preview={preview} vocab={vocab}
                onFilter={(key, values) => setAudience((a) => ({ ...a, [key]: values }))}
                selected={new Set(memberIds)}
                onToggle={(id, name) => setMembers((m) => (m.some((x) => x.id === id)
                  ? m.filter((x) => x.id !== id) : [...m, { id, name }]))}
                onToggleShown={(rows, checked) => setMembers((m) => (checked
                  ? [...m, ...rows.filter((r) => !m.some((x) => x.id === r.id))]
                  : m.filter((x) => !rows.some((r) => r.id === x.id))))}
                emptyNote={chosen.length ? 'Nobody matches every filter.' : 'Nobody here.'} />
              {preview && preview.shown_count > (preview.people.length ?? 0) ? (
                <div className="quiet" style={{ fontSize: 12, marginTop: 6 }}>
                  Showing the first {preview.people.length} of {preview.shown_count.toLocaleString()}.
                </div>
              ) : null}
              {members.length ? (
                <div className="quiet" style={{ fontSize: 12, marginTop: 6 }}>
                  {members.length} ticked by hand — they get it whatever the filters say.
                  <button className="linkbtn" style={{ marginLeft: 6 }} onClick={() => setMembers([])}>Clear</button>
                </div>
              ) : null}

              <div className="aud-row" style={{ marginTop: 10 }}>
                <span className="aud-label">Tagged</span>
                <span className="wb-chips">
                  {(audience.tags ?? []).map((t) => (
                    <span key={t} className="tag">{t}
                      <button onClick={() => setAudience((a) => ({ ...a, tags: (a.tags ?? []).filter((x) => x !== t) }))} aria-label={`Remove ${t}`}>
                        <Icon name="close" size={13} />
                      </button>
                    </span>
                  ))}
                  <input className="tag-input" placeholder="+ tag" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && tagInput.trim()) {
                        setAudience((a) => ({ ...a, tags: [...new Set([...(a.tags ?? []), tagInput.trim()])] }))
                        setTagInput('')
                      }
                    }} />
                </span>
              </div>
              <div className="aud-row" style={{ alignItems: 'flex-start' }}>
                <span className="aud-label">Or add by name</span>
                <div style={{ flex: 1 }}>
                  <PeoplePicker selected={members} onChange={setMembers} />
                </div>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-head"><span className="wb-num">3</span><h2 className="h2">What they get</h2></div>
            <div className="card-body" style={{ paddingTop: 0 }}>
              <select className="field" value={playbook} onChange={(e) => setPlaybook(e.target.value)} disabled={launched} aria-label="Workflow">
                <option value="">Choose a workflow…</option>
                {workflows.some((w) => w.custom) ? (
                  <optgroup label="Yours">
                    {workflows.filter((w) => w.custom).map((w) => <option key={w.key} value={w.key}>{w.name}</option>)}
                  </optgroup>
                ) : null}
                {[...new Set(workflows.filter((w) => !w.custom).map((w) => w.category))].map((c) => (
                  <optgroup key={c} label={c}>
                    {workflows.filter((w) => !w.custom && w.category === c).map((w) => <option key={w.key} value={w.key}>{w.name}</option>)}
                  </optgroup>
                ))}
              </select>
              {launched ? <div className="quiet" style={{ fontSize: 12, marginTop: 4 }}>The workflow is fixed once a drip has launched.</div> : (
                <div className="quiet" style={{ fontSize: 12, marginTop: 4 }}>
                  Need something different? <Link to="/workflows?create=1">Build or describe a new workflow</Link>, then come back.
                </div>
              )}
              {workflow ? <PlanPreview playbook={workflow} /> : null}
              {taskSteps ? (
                <div className="notice warning" style={{ marginTop: 10 }}>
                  <Icon name="task_alt" />
                  <span>This workflow has {taskSteps} task step{taskSteps === 1 ? '' : 's'} — that’s {taskSteps} task{taskSteps === 1 ? '' : 's'} on your list for each person
                    {preview ? ` (${taskSteps * preview.count} in total)` : ''}. Drips usually work best as texts and emails only.</span>
                </div>
              ) : null}
            </div>
          </section>

          <section className="card">
            <div className="card-head"><span className="wb-num">4</span><h2 className="h2">Rules</h2></div>
            <div className="card-body" style={{ paddingTop: 0 }}>
              <div className="rule-sentence" style={{ marginBottom: 12 }}>
                <span>Start at most</span>
                <select className="field" value={dailyLimit} onChange={(e) => setDailyLimit(Number(e.target.value))} aria-label="New people per day" style={{ minWidth: 80 }}>
                  {[...new Set([...PACES, dailyLimit])].sort((a, b) => a - b).map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <span>new people a day, so you can answer replies as they come in.</span>
              </div>
              <label className="checkrow"><input type="checkbox" checked={stopOnReply} onChange={(e) => setStopOnReply(e.target.checked)} />
                <span>Stop for anyone who replies, and give me a task to follow up personally</span></label>
              <div className="rule-sentence" style={{ margin: '10px 0' }}>
                <span>Stop for anyone who moves to</span>
                {(['application', 'funded', 'dead'] as const).map((st) => (
                  <button key={st} className={`chip-btn${stopStatuses.includes(st) ? ' on' : ''}`}
                    onClick={() => setStopStatuses((xs) => xs.includes(st) ? xs.filter((x) => x !== st) : [...xs, st])}>
                    {titleCase(st)}
                  </button>
                ))}
              </div>
              <label className="checkrow"><input type="checkbox" checked={autoJoin} onChange={(e) => setAutoJoin(e.target.checked)} />
                <span>Keep adding people who start matching later <span className="hint">— e.g. every new past client, once their loan funds</span></span></label>
            </div>
          </section>

          <section className="card">
            <div className="card-head"><span className="wb-num">5</span><h2 className="h2">When it sends</h2></div>
            <div className="card-body" style={{ paddingTop: 0 }}>
              <p className="quiet" style={{ fontSize: 12.5, marginTop: 0 }}>
                Starts are spread out inside these hours, at gaps that vary — a campaign that sends
                hundreds of messages back to back, round the clock, is the pattern carriers and spam
                filters act on.
              </p>
              <div className="rule-sentence">
                <span>Send between</span>
                <select className="field" value={pace.window[0]} aria-label="Sending starts at"
                  onChange={(e) => setPace((p) => ({ ...p, window: [Number(e.target.value), p.window[1]] }))}>
                  {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                </select>
                <span>and</span>
                <select className="field" value={pace.window[1]} aria-label="Sending stops at"
                  onChange={(e) => setPace((p) => ({ ...p, window: [p.window[0], Number(e.target.value)] }))}>
                  {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                </select>
                <span>, at least</span>
                <select className="field" value={pace.min_gap_mins} aria-label="Minutes between sends"
                  onChange={(e) => setPace((p) => ({ ...p, min_gap_mins: Number(e.target.value) }))}>
                  {GAPS.map((n) => <option key={n} value={n}>{n} min</option>)}
                </select>
                <span>apart.</span>
              </div>
              <label className="checkrow" style={{ marginTop: 10 }}>
                <input type="checkbox" checked={pace.weekdays_only}
                  onChange={(e) => setPace((p) => ({ ...p, weekdays_only: e.target.checked }))} />
                <span>Weekdays only <span className="hint">— nothing goes out on a Saturday or Sunday</span></span>
              </label>
              <div className="rule-sentence" style={{ marginTop: 10 }}>
                <span>Build up to full pace</span>
                <select className="field" value={pace.ramp_days} aria-label="Ramp up"
                  onChange={(e) => setPace((p) => ({ ...p, ramp_days: Number(e.target.value) }))}>
                  {RAMPS.map(([n, label]) => <option key={n} value={n}>{label}</option>)}
                </select>
              </div>
              <p className="quiet" style={{ fontSize: 12, marginBottom: 0 }}>
                A ramp starts at a quarter of your daily number and climbs — worth it on a new sending
                number, which earns its volume before it carries any.
              </p>
              {preview?.pace_problem ? (
                <div className="notice warning" style={{ marginTop: 10 }}>
                  <Icon name="schedule" /><span>{preview.pace_problem}</span>
                </div>
              ) : null}
            </div>
          </section>

          {err ? <div className="notice danger"><Icon name="error" /><span>{err}</span></div> : null}
        </div>

        <aside className="wb-preview">
          <section className="card">
            <div className="card-head"><h2 className="h2">Who this reaches</h2></div>
            <div className="card-body" style={{ paddingTop: 0 }}>
              {!preview ? <Loading /> : (
                <>
                  <div className="drip-count">{preview.count.toLocaleString()} <span>{preview.count === 1 ? 'person' : 'people'}</span></div>
                  {!preview.has_filter && !members.length ? (
                    <p className="quiet" style={{ fontSize: 12.5 }}>Choose who it goes to on the left. Nobody is included until you do.</p>
                  ) : (
                    <>
                      <div className="drip-reach">
                        <span><Icon name="mail" size={15} /> {preview.reach.email.toLocaleString()} can be emailed</span>
                        <span><Icon name="sms" size={15} /> {preview.reach.text.toLocaleString()} agreed to texts</span>
                      </div>
                      {preview.count === 0 ? (
                        <p className="quiet" style={{ fontSize: 12.5 }}>
                          Nobody matches every row you picked — try removing one.
                          {audience.owner !== 'all' ? (isAdmin
                            ? ' Only contacts assigned to you are included; choose Everyone’s under “Whose contacts” to include the rest.'
                            : ' Only contacts assigned to you are included.') : ''}
                        </p>
                      ) : null}
                      {preview.count ? (
                        <>
                          {preview.schedule ? (
                            <p className="drip-schedule"><Icon name="schedule" size={15} /> <span>{preview.schedule}</span></p>
                          ) : null}
                          <p className="quiet" style={{ fontSize: 12.5 }}>
                            People who can’t be texted or emailed are skipped for that step — it’s noted on their timeline.
                          </p>
                        </>
                      ) : null}
                      {preview.sample.length ? (
                        <div style={{ marginTop: 6 }}>
                          <div className="palette-group" style={{ padding: '4px 0' }}>FOR EXAMPLE</div>
                          {preview.sample.map((p) => (
                            <div key={p.id} style={{ fontSize: 12.5, padding: '3px 0', display: 'flex', gap: 6 }}>
                              <span style={{ flex: 1 }}>{p.name ?? '—'}</span>
                              <span className="quiet">{titleCase(p.status)}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </>
                  )}
                </>
              )}
            </div>
          </section>
        </aside>
      </div>

      {confirming && preview ? (
        <Modal open title="Launch this drip?" width={520} onClose={() => setConfirming(false)}
          footer={<>
            <button className="btn" onClick={() => setConfirming(false)}>Not yet</button>
            <button className="btn primary" onClick={() => save(true)} disabled={busy}>
              <Icon name="send" size={16} /> {busy ? 'Launching…' : `Launch for ${preview.count.toLocaleString()} people`}
            </button>
          </>}>
          <ul className="launch-list">
            <li><strong>{workflow?.name}</strong> starts for <strong>{preview.count.toLocaleString()} {preview.count === 1 ? 'person' : 'people'}</strong>, {dailyLimit} a day — everyone has started within {days} day{days === 1 ? '' : 's'}.</li>
            {preview.schedule ? <li>{preview.schedule}.</li> : null}
            <li>Emails go to the {preview.reach.email.toLocaleString()} who can be emailed; texts only to the {preview.reach.text.toLocaleString()} who agreed to them, never late at night.</li>
            <li>{stopOnReply ? 'Anyone who replies is taken off it and handed to you as a task.' : 'It keeps going even if someone replies.'}</li>
            {autoJoin ? <li>New people who match later are added automatically.</li> : null}
            {meta.messaging.mode === 'simulated' ? <li>Messaging is in <strong>test mode</strong>: messages are recorded but not delivered yet.</li> : null}
          </ul>
          <p className="quiet" style={{ fontSize: 12.5, marginBottom: 0 }}>You can pause or stop it for everyone at any time.</p>
        </Modal>
      ) : null}
    </div>
  )
}

/** Search the lead book and hand-pick people into the drip. */
